function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { batchProdUrl, batchQaUrl } from '../core/types';
import { toQueryString } from './string';
export const MAX_BATCH_SIZE = 5;
// Chrome's maximum payload size for sendBeacon() and keepalive fetch is 64kB
// AEL backend also contains a 100,000 kB limit:
// https://git.corp.stripe.com/stripe-internal/zoolander/blob/HEAD/uppsala/src/main/java/com/stripe/dscore/analyticseventlogger/server/translation/HttpServer.java#L87
const MAX_PAYLOAD_SIZE = 64000;
// Timeout after which to flush the buffer, even if the max batch size isn't reached
const BATCH_TIMEOUT = 3 * 1000;
/**
 * @param AnalyticsConfig
 * The BatchLogger class is used to buffer analytics events to AEL until the MAX_PAYLOAD_SIZE or MAX_BATCH_SIZE is hit.
 * Then, the events are flushed to AEL's batching endpoint at 'r.stripe.com/b'. Events are also flushed
 * when page's visibility state changes.
 */ export class BatchLogger {
    /**
   * Function adds an analytics event to an ongiong list, and flushes the events when the list's length in
   * bytes is greater than MAX_PAYLOAD_SIZE or its size is at batchSize.
   */ pushEvent(event) {
        // if adding event will cause buffer overflow, send current buffer
        if (this.getPayloadSizeInBytes([
            ...this.logEntries,
            event
        ]) >= MAX_PAYLOAD_SIZE) {
            this.fetchLogs(false);
        }
        // add event to buffer
        this.logEntries.push(event);
        // if batch size is reached, send buffer
        if (this.logEntries.length >= this.batchSize) {
            this.fetchLogs(false);
        } else if (!this.batchTimer) {
            // Set a timer to send the buffer if the batch size isn't reached
            this.batchTimer = setTimeout(()=>{
                this.fetchLogs(false);
            }, BATCH_TIMEOUT);
        }
    }
    /**
   * Function returns the buffer of events.
   */ getEvents() {
        return this.logEntries;
    }
    /**
   * Function posts the events to the AEL batching endpoint.
   */ fetchLogs(sendAsync) {
        var _this = this;
        return _async_to_generator(function*() {
            if (_this.logEntries.length === 0) {
                return;
            }
            const numEvents = _this.logEntries.length;
            const params = _this.getLoggerPayload();
            const body = toQueryString(params);
            _this.resetBuffer();
            const typeSuffix = sendAsync ? '-keepalive' : '';
            try {
                const result = yield fetch(_this.trackingUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded'
                    },
                    body,
                    credentials: 'omit',
                    keepalive: sendAsync
                });
                if (!result.ok) {
                    var _this_onAnalyticsRequestFailed, _this1;
                    (_this_onAnalyticsRequestFailed = (_this1 = _this).onAnalyticsRequestFailed) === null || _this_onAnalyticsRequestFailed === void 0 ? void 0 : _this_onAnalyticsRequestFailed.call(_this1, `ael-batch-error${typeSuffix}`, numEvents, new Error(`Error received from HTTP response (${result.status})`));
                }
                return result;
            } catch (err) {
                var _this_onAnalyticsRequestFailed1, _this2;
                (_this_onAnalyticsRequestFailed1 = (_this2 = _this).onAnalyticsRequestFailed) === null || _this_onAnalyticsRequestFailed1 === void 0 ? void 0 : _this_onAnalyticsRequestFailed1.call(_this2, `ael-batch-throws${typeSuffix}`, numEvents, err);
                return null;
            }
        })();
    }
    /**
   * Function returns a request payload adhering to the AEL batching schema.
   */ getLoggerPayload(events = this.logEntries) {
        return {
            client_id: this.clientId,
            num_requests: events.length,
            events: [
                ...events
            ]
        };
    }
    /**
   * Function empties the list of entries.
   */ resetBuffer() {
        this.logEntries = [];
        if (this.batchTimer) {
            clearTimeout(this.batchTimer);
            this.batchTimer = null;
        }
    }
    /**
   * Function returns the size in bytes of the logged events as a URL-encoded string
   */ getPayloadSizeInBytes(events = this.logEntries) {
        const payload = new Blob([
            toQueryString(this.getLoggerPayload(events))
        ]);
        return payload.size;
    }
    /**
   * Function checks if sendBeacon is supported.
   */ // eslint-disable-next-line class-methods-use-this
    isSendBeaconSupported() {
        return typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function';
    }
    /**
   * Function uses navigator.sendBeacon() to send logged events to the trackingUrl.
   */ sendBeaconWithPayload() {
        // sendBeacon returns true if the request has successfully been queued for data transfer.
        // A return value of true does NOT mean that the request was successful
        // sendBeacon may return false if the payload is too big (64kb for Chrome)
        // sendBeacon may throw a TypeError if the navigator context isn't bound to the function
        // for example, https://github.com/jehna/ga-lite/issues/380
        // so, we explicitly bind navigator to sendBeacon to avoid running into "TypeError: Illegal invocation"
        // sendBeacon may also throw an error if the input URL is not parseble
        if (typeof navigator === 'undefined') {
            return false;
        }
        const send = navigator.sendBeacon.bind(navigator);
        try {
            const result = send(this.trackingUrl, new Blob([
                toQueryString(this.getLoggerPayload())
            ], {
                type: 'application/x-www-form-urlencoded'
            }));
            if (!result) {
                var _this_onAnalyticsRequestFailed, _this;
                (_this_onAnalyticsRequestFailed = (_this = this).onAnalyticsRequestFailed) === null || _this_onAnalyticsRequestFailed === void 0 ? void 0 : _this_onAnalyticsRequestFailed.call(_this, 'ael-beacon-error', this.logEntries.length);
            }
            return result;
        } catch (err) {
            var _this_onAnalyticsRequestFailed1, _this1;
            (_this_onAnalyticsRequestFailed1 = (_this1 = this).onAnalyticsRequestFailed) === null || _this_onAnalyticsRequestFailed1 === void 0 ? void 0 : _this_onAnalyticsRequestFailed1.call(_this1, 'ael-beacon-throws', this.logEntries.length, err);
            // return false to default to fetch method
            return false;
        }
    }
    /**
   * Function uses sendBeacon() to send requests. If sendBeacon() isn't supported or returns false,
   * function fallbacks to using fetch instead with keepAlive set to true.
   */ handleLogging() {
        let sendBeaconSuccess = false;
        if (this.isSendBeaconSupported()) {
            sendBeaconSuccess = this.sendBeaconWithPayload();
        }
        // Fallback to fetch with keepAlive if sendBeacon isn't supported or the data queuing failed.
        if (!sendBeaconSuccess) {
            this.fetchLogs(true);
        }
        this.resetBuffer();
    }
    constructor(config){
        var _config_batchSettings, _config_batchSettings1;
        _define_property(this, "logEntries", []);
        _define_property(this, "clientId", void 0);
        _define_property(this, "trackingUrl", void 0);
        _define_property(this, "batchSize", void 0);
        _define_property(this, "batchTimer", void 0);
        _define_property(this, "onAnalyticsRequestFailed", void 0);
        this.clientId = config.clientId;
        this.trackingUrl = config.inProduction ? batchProdUrl : batchQaUrl;
        this.batchSize = ((_config_batchSettings = config.batchSettings) === null || _config_batchSettings === void 0 ? void 0 : _config_batchSettings.batchSize) ? (_config_batchSettings1 = config.batchSettings) === null || _config_batchSettings1 === void 0 ? void 0 : _config_batchSettings1.batchSize : MAX_BATCH_SIZE;
        this.batchTimer = null;
        this.onAnalyticsRequestFailed = config.onAnalyticsRequestFailed;
        // sendBeacon asynchronously sends analytics data to the AEL batching endpoint
        // when the user moves away from the page
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', (_evt)=>{
                if (document.visibilityState === 'hidden' && this.logEntries.length > 0) {
                    this.handleLogging();
                }
            });
        }
    }
}

//# sourceMappingURL=batchLogger.js.map